import { connect } from 'react-redux';
import React, { Component } from 'react';
import { bindActionCreators, Dispatch, AnyAction } from 'redux';

import { Link } from '@tcc/crate';
import { getMessage } from '@tcc/i18n';

import { PanelCards } from '../../Panel';
import { FHAPreview } from '../../../components/cards/FHA/FHAPreview';
import FHAEvents from './FHAEvents';

import {
  getCurrentFHAEvent,
  nextEventSelector,
  activeSubLayerSelector,
  prevEventSelector,
  hasHistoricWeatherSelector,
} from '../../../selectors/fhaSelectors';
import { getFeatureValue } from '../../../selectors/featureSelectors';
import { setActiveSubLayer } from '../../../actions/layersActions';
import { reqWeatherData } from '../../../actions/weatherActions';
import { openModal } from '../../../actions/modalActions';

import { calcPrecipitation } from '../../../utils/weatherData';
import { FHA, FHA_KEY } from '../../../constants/mapLayerIds';
import { APPLICATION, HARVEST, PLANTING, WEATHER } from '../../../constants/cards';
import { AppState } from '../../../store/types/Store';
import { FeatureFlagKey } from '../../../types/FeatureFlag';

interface FHAEvent {
  date: string;
  gdus?: number;
  [key: string]: unknown;
}

interface SubLayer {
  date: string;
  layer?: string;
  segmentSelected: string;
}

interface OwnProps {
  index: number;
  isCollapsed: boolean;
  isSplitViewEnabled: boolean;
  collapsePanel?: () => void;
}

interface StateProps {
  currentSeason: string;
  currentEvent: FHAEvent | undefined;
  currentSubLayer: SubLayer | undefined;
  hasHistoricWeather: boolean;
  microsoftGDDGlobalRelease: boolean;
  nextEvent: FHAEvent | undefined;
  plantingDate: string | undefined;
  precipitationDates: Record<string, unknown>;
  prevEvent: FHAEvent | undefined;
  locale: string;
}

interface DispatchProps {
  setActiveSubLayer: typeof setActiveSubLayer;
  reqWeatherData: typeof reqWeatherData;
  openModal: typeof openModal;
}

type FHAPanelProps = OwnProps & StateProps & DispatchProps;

const CARDS = [WEATHER, APPLICATION, PLANTING, HARVEST];

export class FHAPanel extends Component<FHAPanelProps> {
  UNSAFE_componentWillMount() {
    const {
      reqWeatherData: reqWeatherDataProp,
    } = this.props;

    reqWeatherDataProp();
  }

  onRightClickPreview = () => {
    const {
      nextEvent,
      currentSubLayer,
      index,
      currentSeason,
      setActiveSubLayer: setActiveSubLayerProp,
    } = this.props;
    if (!nextEvent || !currentSubLayer) return;
    const activeSubLayer = {
      ...currentSubLayer,
      date: nextEvent.date,
    };
    setActiveSubLayerProp(FHA_KEY, activeSubLayer, index, currentSeason);
  };

  onLeftClickPreview = () => {
    const {
      index,
      prevEvent,
      currentSeason,
      currentSubLayer,
      setActiveSubLayer: setActiveSubLayerProp,
    } = this.props;
    if (!prevEvent || !currentSubLayer) return;

    const activeSubLayer = {
      ...currentSubLayer,
      date: prevEvent.date,
    };

    setActiveSubLayerProp(FHA_KEY, activeSubLayer, index, currentSeason);
  };

  onToggleType = (e: React.MouseEvent) => {
    const {
      currentSubLayer,
      currentSeason,
      index,
      setActiveSubLayer: setActiveSubLayerProp,
    } = this.props;
    if (!currentSubLayer) return;
    const layerType =
      currentSubLayer.segmentSelected === FHA.SCOUTING
        ? FHA.VEGETATION
        : FHA.SCOUTING;
    const activeSubLayer = {
      ...currentSubLayer,
      layer: layerType,
      segmentSelected: layerType,
    };
    e.preventDefault();
    setActiveSubLayerProp(FHA_KEY, activeSubLayer, index, currentSeason);
  };

  render() {
    const {
      currentEvent,
      currentSubLayer,
      hasHistoricWeather,
      microsoftGDDGlobalRelease,
      index,
      isCollapsed,
      isSplitViewEnabled,
      nextEvent,
      plantingDate,
      precipitationDates,
      prevEvent,
      locale,
      collapsePanel,
    } = this.props;

    if (!currentEvent || !currentSubLayer) {
      return (
        <PanelCards
          cards={CARDS}
          index={index}
          isCollapsed={isCollapsed}
          testId="fha"
        />
      );
    }

    const precipitation = calcPrecipitation(
      precipitationDates,
      currentEvent.date,
      plantingDate
    );

    const firstCard =
      isSplitViewEnabled && isCollapsed ? (
        <FHAPreview
          date={currentEvent.date}
          gdus={currentEvent.gdus}
          hasHistoricWeather={hasHistoricWeather}
          microsoftGDDGlobalRelease={microsoftGDDGlobalRelease}
          locale={locale}
          lowPercent={(currentEvent[FHA.SCOUTING] as { lowPercent: number })?.lowPercent}
          onLeftClick={
            prevEvent && prevEvent.date === currentEvent.date
              ? () => { /* noop */ }
              : this.onLeftClickPreview
          }
          onRightClick={
            nextEvent && nextEvent.date === currentEvent.date
              ? () => { /* noop */ }
              : this.onRightClickPreview
          }
          precipitation={precipitation}
        />
      ) : (
        <FHAEvents
          index={index}
          collapsePanel={collapsePanel}
        />
      );

    const layerType =
      currentSubLayer.segmentSelected === FHA.SCOUTING
        ? getMessage('fha.scouting')
        : getMessage('fha.vegetation');

    const typeToggle = (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <Link onClick={this.onToggleType} testId="fha-toggle-type">
        {layerType}
      </Link>
    );

    return (
      <PanelCards
        cards={CARDS}
        firstCard={firstCard}
        headerDetail={isCollapsed ? typeToggle : undefined}
        index={index}
        isCollapsed={isCollapsed}
        testId="fha"
      />
    );
  }
}

function mapStateToProps(state: AppState, props: OwnProps): StateProps {
  const season = state.seasons.activeSeasons[props.index].id;
  const plantingData = state.planting[season];

  return {
    currentSeason: state.seasons.activeSeasons[props.index].id,
    currentEvent: getCurrentFHAEvent(state, props),
    currentSubLayer: activeSubLayerSelector(state, props),
    hasHistoricWeather: hasHistoricWeatherSelector(state),
    microsoftGDDGlobalRelease: !!getFeatureValue(state, { key: FeatureFlagKey.MICROSOFT_WEATHER_GDD_GLOBAL_RELEASE }),
    nextEvent: nextEventSelector(state, props),
    plantingDate: (plantingData as { date?: string } | undefined)?.date,
    precipitationDates: state.weather.precipitation,
    prevEvent: prevEventSelector(state, props),
    locale: state.app.locale,
  };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>): DispatchProps {
  return bindActionCreators(
    { setActiveSubLayer, reqWeatherData, openModal },
    dispatch
  ) as unknown as DispatchProps;
}

export default connect(mapStateToProps, mapDispatchToProps)(FHAPanel);
